import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, Input } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { AppService } from 'src/@core/services/app.service';
import { AuthService } from 'src/@core/services/auth.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { CallAgentComponent } from 'src/app/modules/agents/call-agent/call-agent.component';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { GoogleIntegrationService } from 'src/@core/services/job/google-integration.service';
import { from, of } from 'rxjs';
import { gapi } from 'gapi-script';
import { ChangeDetectorRef } from '@angular/core';
import { CreateLeadsUserComponent } from '../view-project/create-leads-user/create-leads-user.component';
import { ThemeService } from 'src/@core/services/theme.service';
import { environment } from 'src/environments/environment';
import { LayoutService } from 'src/@core/services/theme/layout.service';

/** `?tab=` values, in tab order. */
const TAB_PARAMS = ['search', 'connected', 'assigned', 'tasks', 'funnel', 'loop', 'agent-evals'];
const FUNNEL_TAB_INDEX = TAB_PARAMS.indexOf('funnel');
const LOOP_TAB_INDEX = TAB_PARAMS.indexOf('loop');
const AGENT_EVALS_TAB_INDEX = TAB_PARAMS.indexOf('agent-evals');

@Component({
  selector: 'hour4u-admin-view-project-new',
  templateUrl: './view-project-new.component.html',
  styleUrls: ['./view-project-new.component.scss']
})
export class ViewProjectNewComponent implements OnInit {

  projectId: any = null;
  callData: any = null;
  profile = this.appService.getCurrentAccount();
  private intervalId: any;
  diameter = 40;
  loaderColor: string;
  mode = 'indeterminate';
  isLoading: boolean = false;
  dataSource = [];
  projectDetails: any;
  metadata: any;
  pagination: any;
  /** Search tab — token-based leads pagination (GET /hoaProjectLeads) */
  pageIndex: number = 1;
  pageSize: number = 50;
  /** Opaque cursor from previous page; never parse/construct. */
  leadsNextToken: string | null = null;
  leadsHasNextPage = false;
  isLoadingMoreLeads = false;
  /** Server-side lead filters (GET /hoaProjectLeads?name=&mobile=) */
  leadsSearchName: string | null = null;
  leadsSearchMobile: string | null = null;
  /** Connected tab pagination (call history) driven by URL (?clPage= & ?clPageSize=) */
  connectedPageIndex: number = 1;
  connectedPageSize: number = 10;
  pageSizeOptions: number[] = [10, 20, 50, 100, 200, 500];
  currentRange: string = '';
  googleTokenExpired: boolean = false;
  CLIENT_ID: string = environment.googleClientId;
  selectedTabIndex: number = 0;  // Default selected tab index
  /** Arya chat side panel. Kept mounted once opened so a running turn keeps polling. */
  agentOpen = false;
  agentMounted = false;

  toggleAgent(open: boolean) {
    this.agentOpen = open;
    if (open) {
      this.agentMounted = true;
    }
  }

  /** Arya changed the project (prompt, outcome, setting). Load the fresh copy quietly:
   *  no page spinner, and the chat keeps its place because it is the same project. */
  reloadProjectAfterAgentEdit() {
    if (!this.profile || !this.profile.entity || !this.projectId) {
      return;
    }
    this.jobService.getHoaProject(this.profile.entity.id, this.projectId).subscribe(
      (response) => {
        if (response && response.project_id) {
          this.projectDetails = response;
          if (response.project_name) {
            this.layoutService.setProjectDetails({ project_name: response.project_name });
          }
        }
      },
      () => { /* the next visit loads it anyway */ }
    );
  }
  /** Shape: { leads, pagination } — leads come from /hoaProjectLeads only */
  apolloLeads: any;
  googleScope: string = environment.googleScope;
  workflowTabTriggered: boolean = false;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private notify: NotificationService,
    private appService: AppService,
    public enterpriseAdminService: EnterpriseAdminService,
    private dialog: MatDialog,
    private jobService: JobService,
    private googleIntegrationService: GoogleIntegrationService,
    private cdr: ChangeDetectorRef,
    public themeService: ThemeService,
    public router: Router,
    private layoutService: LayoutService,
  ) { }

  ngOnInit() {
    this.initClientGoogle();
    //this.layoutService.collapseSidenav();

    // Keep tab + pagination in sync with URL
    this.route.queryParamMap.subscribe(params => {
      this.applyConnectedQueryParams(params);
      this.applyLeadsQueryParams(params);
      this.selectedTabIndex = this.tabIndexFromParam(params.get('tab'));

      // If project is already loaded and we're on Connected tab, reload call history
      if (this.selectedTabIndex === 1 && this.projectDetails && this.projectDetails.project_id) {
        this.getDBCallsHistory(this.connectedPageSize, this.connectedPageIndex);
      }

      // If project is already loaded and we're on Search tab, reload leads from first page
      // (URL leadPage is display-only; real paging uses next_token)
      if (this.selectedTabIndex === 0 && this.projectDetails && this.projectDetails.project_id) {
        // Only hard-reload when page size changed via URL; avoid re-fetch on every query tick
        const sizeRaw = params.get('leadPageSize');
        const nextSize = sizeRaw ? parseInt(sizeRaw, 10) : this.pageSize;
        if (Number.isFinite(nextSize) && nextSize > 0 && nextSize !== this.pageSize) {
          this.pageSize = nextSize;
          this.loadLeadsPage(true);
        }
      }
    });

    const currentNav = this.router.getCurrentNavigation();
    const navState = currentNav && currentNav.extras && currentNav.extras.state ? currentNav.extras.state : history.state;
    const projectFromState = navState && navState.project;
    const clonedProjectFromState = navState && navState.createProjectPayloadRaw && navState.isCloned;

    if (projectFromState) {
      this.projectId = projectFromState.project_id;
      // Bounded poll while newly created project becomes available (max ~30s)
      let createPollAttempts = 0;
      const maxCreatePollAttempts = 30;
      const checkProjectInterval = setInterval(() => {
        createPollAttempts++;
        if (createPollAttempts > maxCreatePollAttempts) {
          clearInterval(checkProjectInterval);
          console.warn('Stopped waiting for new project after max attempts');
          return;
        }
        this.jobService.getHoaProject(this.profile.entity.id, this.projectId).subscribe(
          (response) => {
            if (response && response.project_id) {
              clearInterval(checkProjectInterval);
              this.projectDetails = response;
              // Set project details in layout service for toolbar
              if (response.project_name) {
                this.layoutService.setProjectDetails({ project_name: response.project_name });
              }
              // Leads no longer come from hoaProject — clear any embedded array
              this.dataSource = [];
              this.apolloLeads = { leads: [], pagination: null };

              // Campaign already ran → default to Connected tab (unless user picked a tab)
              if (this.redirectToConnectedIfCampaignRan(response)) {
                return;
              }

              const queryParams = this.route.snapshot.queryParams;
              this.selectedTabIndex = this.tabIndexFromParam(queryParams['tab']);
              this.applyConnectedQueryParams(this.route.snapshot.queryParamMap);
              this.applyLeadsQueryParams(this.route.snapshot.queryParamMap);

              if (this.selectedTabIndex === 1) {
                this.getDBCallsHistory(this.connectedPageSize, this.connectedPageIndex);
              } else {
                this.loadLeadsPage(true);
              }
            }
          },
          (error) => {
            // Suppress notify.showMessage() and let the loader spin silently
            console.log('Waiting for project to be ready...');
          }
        );
      }, 1000);
    } else if (clonedProjectFromState) {
      console.log('🔄 Handling cloned project:', navState.createProjectPayloadRaw.projectId);
      // Handle cloned project - set projectId from the cloned payload
      this.projectId = navState.createProjectPayloadRaw.projectId;
      // For cloned projects, we don't need to poll since the project creation will be handled by view-project-details-new
      // Just set the basic project details from the cloned data
      this.projectDetails = {
        project_id: navState.createProjectPayloadRaw.projectId,
        project_name: navState.createProjectPayloadRaw.projectDescription + ' (Clone)',
        project_description: navState.createProjectPayloadRaw.projectDescription,
        agent_id: navState.createProjectPayloadRaw.agentId,
        agent_name: navState.createProjectPayloadRaw.agentName,
        use_apollo: navState.createProjectPayloadRaw.useApollo,
        leads: [], // Empty leads array for cloned project
        user_prompt: {} // Empty user_prompt for cloned project
      };
      this.apolloLeads = this.projectDetails;

      console.log('✅ Cloned project details set:', this.projectDetails);

      const queryParams = this.route.snapshot.queryParams;
      this.selectedTabIndex = this.tabIndexFromParam(queryParams['tab']);
      this.applyConnectedQueryParams(this.route.snapshot.queryParamMap);
      this.applyLeadsQueryParams(this.route.snapshot.queryParamMap);
    } else {
      this.route.params.pipe(
        switchMap(result => {
          this.projectId = result.id;
          return this.route.queryParams.pipe(
            switchMap(params => {
              this.selectedTabIndex = this.tabIndexFromParam(params['tab']);
              return this.getProjectById(this.profile.entity.id, this.projectId);
            })
          );
        })
      ).subscribe((project) => {
        if (!project) {
          return;
        }

        // Campaign already ran → default to Connected tab (unless user picked a tab)
        if (this.redirectToConnectedIfCampaignRan(project)) {
          return;
        }

        if (this.selectedTabIndex === 1) {
          this.applyConnectedQueryParams(this.route.snapshot.queryParamMap);
          this.getDBCallsHistory(this.connectedPageSize, this.connectedPageIndex);
          return;
        }
        if (this.selectedTabIndex === 0) {
          this.applyLeadsQueryParams(this.route.snapshot.queryParamMap);
          this.loadLeadsPage(true);
        }
      });
    }
  }

  initClientGoogle() {
    gapi.load('client:auth2', () => {
      gapi.auth2
        .init({
          client_id: this.CLIENT_ID,
          cookiepolicy: 'single_host_origin',
          scope: this.googleScope,
          access_type: 'offline',
          // refre
        })
        .then(() => {
          console.log('Google API client initialized');
        })
        .catch((error) => {
          console.error('Error initializing Google API client:', error);
        });
    });
  };

  refreshGoogleToken() {
    const authInstance = gapi.auth2.getAuthInstance();
    from(
      authInstance.grantOfflineAccess({
        prompt: 'consent',
      })
    ).pipe(
      switchMap((response: any) => {
        const authCode = response.code;
        console.log('Authorization Code:', authCode);

        // Call the first API to exchange the auth code for tokens
        return this.googleIntegrationService.generateRefreshToken({ code: authCode });
      }),
      switchMap((tokenResponse) => {
        console.log('Token received:', tokenResponse);

        // Now, call the second API with the received token
        let obj = {
          entityId: this.profile.entity.id,
          userId: this.profile.user.id,
          userName: this.profile.user.userMeta.name,
          projectId: this.projectDetails.project_id,
          googleCalendarToken: tokenResponse.refresh_token,
        };

        return this.jobService.updateProject(obj);
        // return this.googleIntegrationService.validateToken({ refresh_token: tokenResponse.refresh_token });
      })
    ).subscribe(
      (validationResponse) => {
        console.log('Google API Validation Response:', validationResponse);
        this.googleTokenExpired = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error during API calls:', error);
        // this.showErrorMessage('Google API request failed. Please try again.');
      }
    );
  }

  uploadData() {
    const dialogRef = this.dialog.open(CallAgentComponent, {
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      autoFocus: true,
      minWidth: "500px",
      data: {
        projectId: this.projectDetails.project_id
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(result)
      if (result) {
        this.getAllCalls();
      }
    });
  }

  addUser() {
    const dialogRef = this.dialog.open(CreateLeadsUserComponent, {
      panelClass: 'create-lead-dialog',
      disableClose: true,
      autoFocus: true,
      width: '560px',
      maxWidth: '95vw',
      data: {
        project_id: this.projectDetails.project_id
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(result)
      if (result) {
        this.getAllCalls();
      }
    });
  }

  getProjectById(entityId: string, projectId: string) {
    this.isLoading = true;
    return this.jobService.getHoaProject(entityId, projectId).pipe(
      tap((response) => {
        console.log('project details retrieved successfully:', response);
        this.projectDetails = response;
        // Set project details in layout service for toolbar
        if (response && response.project_name) {
          this.layoutService.setProjectDetails({ project_name: response.project_name });
        }
        // Metadata only — do not seed leads from hoaProject
        if (!this.apolloLeads) {
          this.apolloLeads = { leads: [], pagination: null };
        }
        this.isLoading = false;
      }),
      catchError(err => {
        this.isLoading = false;
        return of(null);
      })
    );
  }

  private getAllCalls() {
    this.dataSource = [];
    this.getDBCallsHistory(this.connectedPageSize, this.connectedPageIndex);
  }

  getDBCallsHistory(maxPageSize: number, pageNum: number) {
    console.log("getDBCallsHistory")
    this.isLoading = true;
    this.enterpriseAdminService.getCallHistoryProject(this.profile.entity.id, this.projectDetails.project_id, maxPageSize, pageNum).subscribe(res => {
      this.isLoading = false;
      if (res && res.records && res.records.length) {
        this.dataSource = res.records;
        this.pagination = res.pagination;
        this.metadata = res.metadata;
        console.log(this.metadata)
        this.dataSource.forEach(ele => {
          if (ele.start_time) {
            ele.start_time = this.formatStartTime(ele.start_time);
          }
        })
        console.log("res", res);
        this.callData = res;
        console.log(this.dataSource);
      }
    }, (err => {
      this.isLoading = false;
      console.log(err);
    }))
  }

  formatStartTime(value: any): string {
    const timestamp = parseFloat(value) * 1000; // Convert to milliseconds
    const date = new Date(timestamp);

    // Get the day, month, and year
    const day = date.getDate();
    const month = this.getMonthAbbreviation(date.getMonth()); // Get month abbreviation (e.g., "Dec")
    const year = date.getFullYear();

    // Get the time components (hours, minutes, seconds)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Format time in 12-hour format with AM/PM
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = this.padNumber(minutes);
    const formattedSeconds = this.padNumber(seconds);

    // Return formatted string like 'Dec 31, 2024, 5:04:39 PM'
    return `${month} ${day}, ${year}, ${formattedHours}:${formattedMinutes}:${formattedSeconds} ${period}`;
  }

  // Helper method to get month abbreviation (e.g., 'Jan' for January)
  getMonthAbbreviation(month: number): string {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return months[month] || '';
  }

  // Helper method to pad single digits for minutes and seconds
  padNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  onPageChange(event: { pageNum: number; pageSize: number; loadMore?: boolean; reset?: boolean }) {
    const { pageNum, pageSize } = event;
    console.log('Page Number:', pageNum, 'Page Size:', pageSize, event);
    // Connected tab: numbered call-history pagination
    if (this.selectedTabIndex === 1) {
      this.navigateQuery({ tab: 'connected', clPage: pageNum, clPageSize: pageSize });
      return;
    }

    // Search tab: token-based leads pagination
    if (pageSize && pageSize !== this.pageSize) {
      this.pageSize = pageSize;
      this.loadLeadsPage(true);
      return;
    }
    if (event.loadMore) {
      this.loadLeadsPage(false);
      return;
    }
    if (event.reset !== false) {
      this.loadLeadsPage(true);
    }
  }

  fetchRecruitmentCandidates() {
    this.isLoading = true;

    const userPrompt = this.projectDetails.user_prompt;
    const jobDescriptionWithExperience = `${userPrompt["Job Description"]} with ${userPrompt["Experience Level"]}`;

    this.enterpriseAdminService.fetchRecruitmentCandidates(this.projectDetails.project_id, this.profile.entity.id, jobDescriptionWithExperience).subscribe(res => {
      console.log(res);
      this.getDBCallsHistory(this.pageSize, this.pageIndex);
      this.getProjectById(this.profile.entity.id, this.projectId);
      this.isLoading = false;

    }, (err => {
      console.log(err);
    }))
  }

  onTabChanged(event) {
    this.selectedTabIndex = event.index;

    const tabParam = TAB_PARAMS[this.selectedTabIndex] || 'search';

    this.navigateQuery({ tab: tabParam });

    if (this.selectedTabIndex === 0) {
      this.applyLeadsQueryParams(this.route.snapshot.queryParamMap);
      this.loadLeadsPage(true);
    } else if (this.selectedTabIndex === 1) {
      // Ensure we read clPage/clPageSize from URL when switching to Connected
      this.applyConnectedQueryParams(this.route.snapshot.queryParamMap);
      this.getDBCallsHistory(this.connectedPageSize, this.connectedPageIndex);
    }
  }
  private tabIndexFromParam(tab: string | null): number {
    const index = tab === 'settings' ? FUNNEL_TAB_INDEX : TAB_PARAMS.indexOf(tab);
    return index === -1 ? 0 : index;
  }

  goToProjects() {
    this.router.navigate(['/job']);
  }


  get funnelTabIndex(): number { return FUNNEL_TAB_INDEX; }
  get loopTabIndex(): number { return LOOP_TAB_INDEX; }
  get agentEvalsTabIndex(): number { return AGENT_EVALS_TAB_INDEX; }

  /** Funnel strip -> Funnel tab, keeping the URL in step. */
  openFunnelTab() {
    this.selectedTabIndex = FUNNEL_TAB_INDEX;
    this.navigateQuery({ tab: TAB_PARAMS[FUNNEL_TAB_INDEX] });
  }

  /** Funnel and Loop tabs -> project edit, goals step where these settings live. */
  openSettingsTab() {
    if (!this.projectDetails || !this.projectDetails.project_id) { return; }
    void this.router.navigate(['/job/view-new-project', this.projectDetails.project_id, 'setup-workflow'], {
      queryParams: { step: 'goals' }
    });
  }

  onTabChangeFromChild(tabName: string) {
    if (tabName === 'connected') {
      this.selectedTabIndex = 1;
      this.workflowTabTriggered = true;
    }
  }

  private applyConnectedQueryParams(params: ParamMap): void {
    const pageRaw = params.get('clPage');
    const sizeRaw = params.get('clPageSize');

    const p = pageRaw ? parseInt(pageRaw, 10) : 1;
    this.connectedPageIndex = Number.isFinite(p) && p >= 1 ? p : 1;

    const s = sizeRaw ? parseInt(sizeRaw, 10) : 10;
    this.connectedPageSize = Number.isFinite(s) && s >= 1 ? s : 10;
  }

  private applyLeadsQueryParams(params: ParamMap): void {
    const sizeRaw = params.get('leadPageSize');
    const s = sizeRaw ? parseInt(sizeRaw, 10) : this.pageSize;
    this.pageSize = Number.isFinite(s) && s >= 1 ? Math.min(s, 200) : 50;
  }

  /**
   * Load project leads from GET /hoaProjectLeads.
   * @param reset true = first page (drop token); false = append using next_token
   */
  private loadLeadsPage(reset: boolean = true): void {
    if (!(this.projectDetails && this.projectDetails.project_id)) {
      return;
    }

    if (!reset && !this.leadsHasNextPage) {
      return;
    }
    if (!reset && !this.leadsNextToken) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.leadsNextToken = null;
      this.leadsHasNextPage = false;
      this.pageIndex = 1;
    } else {
      this.isLoadingMoreLeads = true;
    }

    const entityId =
      (this.profile && this.profile.entity && this.profile.entity.id) ||
      this.projectDetails.entity_id;

    // Totals are null whenever a filter is applied, so don't pay for the count.
    const filtered = this.isLeadsFiltered();

    this.jobService
      .getHoaProjectLeads({
        projectId: this.projectDetails.project_id,
        pageSize: this.pageSize || 50,
        nextToken: reset ? null : this.leadsNextToken,
        order: 'desc',
        includeTotal: !filtered,
        entityId,
        name: this.leadsSearchName,
        mobile: this.leadsSearchMobile
      })
      .subscribe(
        (response) => {
          this.isLoading = false;
          this.isLoadingMoreLeads = false;
          this.applyLeadsResponse(response, reset);
        },
        (error) => {
          this.isLoading = false;
          this.isLoadingMoreLeads = false;
          const message =
            (error && error.error && error.error.message) ||
            (error && error.message) ||
            '';
          const status = error && error.status;
          // Invalid next_token → drop cursor and reload first page
          if (status === 400 && !reset) {
            console.warn('Invalid leads next_token; resetting to first page.', message);
            this.leadsNextToken = null;
            this.leadsHasNextPage = false;
            this.loadLeadsPage(true);
            return;
          }
          console.error('Error fetching project leads:', error);
        }
      );
  }

  private applyLeadsResponse(response: any, reset: boolean): void {
    const pageLeads = (response && response.leads) ? response.leads : [];
    const pagination = (response && response.pagination) ? response.pagination : null;

    const prevLeads =
      !reset && this.apolloLeads && Array.isArray(this.apolloLeads.leads)
        ? this.apolloLeads.leads
        : [];
    const leads = reset ? pageLeads : prevLeads.concat(pageLeads);

    this.dataSource = leads;
    this.pagination = pagination;
    this.apolloLeads = {
      leads,
      pagination,
      // Keep project-level records merge support if present on prior object
      records: this.apolloLeads && this.apolloLeads.records ? this.apolloLeads.records : undefined
    };

    // Opaque cursor for next fetch
    const nextToken =
      pagination && (pagination.next_token != null ? pagination.next_token : pagination.nextToken);
    this.leadsNextToken = nextToken ? String(nextToken) : null;
    this.leadsHasNextPage = !!(
      pagination &&
      (pagination.has_next_page === true || pagination.has_more === true)
    ) && !!this.leadsNextToken;

    // Echo page number for display only when backend provides it
    if (pagination) {
      const pageEcho =
        pagination.page != null
          ? Number(pagination.page)
          : pagination.current_page != null
            ? Number(pagination.current_page)
            : pagination.currentPageNum != null
              ? Number(pagination.currentPageNum)
              : null;
      if (pageEcho != null && Number.isFinite(pageEcho)) {
        this.pageIndex = pageEcho;
      } else if (reset) {
        this.pageIndex = 1;
      } else {
        this.pageIndex = (this.pageIndex || 1) + 1;
      }

      const sizeEcho =
        pagination.page_size != null
          ? Number(pagination.page_size)
          : pagination.currentPageSize != null
            ? Number(pagination.currentPageSize)
            : null;
      if (sizeEcho != null && Number.isFinite(sizeEcho) && sizeEcho > 0) {
        this.pageSize = sizeEcho;
      }
    }

    this.cdr.markForCheck();
  }

  /** True while a name/mobile filter is narrowing the leads list. */
  isLeadsFiltered(): boolean {
    return !!(this.leadsSearchName || this.leadsSearchMobile);
  }

  /**
   * Server-side lead search. Resets the cursor and refetches from the first page —
   * next_token is only valid for the query that produced it.
   */
  onLeadsSearch(event: { name?: string | null; mobile?: string | null }): void {
    const name = ((event && event.name) || '').trim();
    const mobile = ((event && event.mobile) || '').trim();

    if (name === (this.leadsSearchName || '') && mobile === (this.leadsSearchMobile || '')) {
      return;
    }

    this.leadsSearchName = name || null;
    this.leadsSearchMobile = mobile || null;
    this.loadLeadsPage(true);
  }

  /** Public refresh used after manual lead create / upload */
  refreshProjectLeads(): void {
    this.loadLeadsPage(true);
  }

  private navigateQuery(queryParams: Params): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  /**
   * True when a campaign has actually produced contact activity (voice and/or WhatsApp).
   *
   * Strict on purpose — do NOT use loose signals such as:
   * - total_leads (only means leads were uploaded)
   * - total_credit_used (credits can move without a real campaign run)
   * - call duration alone (incomplete; WhatsApp campaigns may have 0 duration)
   *
   * Count as "ran" only when we have real connected contacts or campaign outcomes.
   * That covers both call and WhatsApp outreach.
   */
  private hasCampaignRun(project: any): boolean {
    if (!project) {
      return false;
    }
    const n = (value: any): number => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    const connectedLeads =
      n(project.total_connected_leads) > 0 ||
      n(project.agg_total_connected_leads) > 0;

    const progress = project.campaign_progress;
    const achievedPeople =
      progress && Array.isArray(progress.achieved_people_ids)
        ? progress.achieved_people_ids.length
        : 0;
    const campaignOutcomes =
      n(progress && progress.achieved_count) > 0 || achievedPeople > 0;

    return connectedLeads || campaignOutcomes;
  }

  /**
   * When the user lands without an explicit ?tab= and the campaign has already run,
   * send them to Connected instead of the default Search tab.
   * Returns true if a redirect was issued (caller should skip loading the Search tab).
   */
  private redirectToConnectedIfCampaignRan(project: any): boolean {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    // Respect an explicit tab choice from the user / deep link
    if (TAB_PARAMS.indexOf(tab) !== -1) {
      return false;
    }
    if (!this.hasCampaignRun(project)) {
      return false;
    }

    this.selectedTabIndex = 1;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'connected' },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    return true;
  }
}